import {userTimezone} from '../../user/helper/helperFunction'
import {AuthModel} from './_models'
import { navigateToLogin } from './NavigationHelper'
import { getCurrentApiUrl } from '../../../utils/backendConfig'

const getApiUrl = () => getCurrentApiUrl()
const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'

// get auth
const getAuth = (): any | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }
  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // Handle legacy auth_token format for backwards compatibility
      if (auth.auth_token && !auth.token) {
        auth.token = auth.auth_token
        // Update localStorage with correct format
        setAuth(auth)
      }
      
      // Remove user data from localStorage if it exists (cleanup for new approach)
      if (auth.user) {
        delete auth.user
        setAuth(auth) // Save cleaned version
      }
      
      return auth
    }
  } catch (error) {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  }
}

// set auth
const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }
  try {
    const lsValue = JSON.stringify({
      token: auth.token,                    // Node.js JWT token (might be undefined)
      laravel_token: auth.laravel_token,   // Laravel Sanctum token (might be undefined)
      // user: auth.user                   // ❌ Removed: User data will be fetched via /auth/me API
    })
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error(error ?? 'An unexpected error occurred, please try again later')
  }
}

// remove auth
const removeAuth = () => {
  if (!localStorage) {
    return
  }
  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    // console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

// Auth context helper to update user data from interceptor
let updateCurrentUserCallback: ((user: any) => void) | null = null
let isUpdatingUser = false

export function setUpdateCurrentUserCallback(callback: (user: any) => void) {
  updateCurrentUserCallback = callback
}

export function updateCurrentUserFromInterceptor(userData: any) {
  if (updateCurrentUserCallback && !isUpdatingUser) {
    isUpdatingUser = true
    try {
      updateCurrentUserCallback(userData)
    } finally {
      // Reset flag after a short delay to prevent rapid updates
      setTimeout(() => {
        isUpdatingUser = false
      }, 50)
    }
  }
}

// setup axios(interceptor)
export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.defaults.baseURL = getApiUrl()
  
  axios.interceptors.request.use(
    async (config: any) => {
      const auth = getAuth()
      if (auth) {
        // Use Laravel token if available, otherwise fall back to regular token
        const tokenToUse = auth.laravel_token ?? auth.token
        if (tokenToUse && config.headers) {
          config.headers['auth-token'] = tokenToUse
          // Always set Authorization header for Bearer token authentication
          config.headers['Authorization'] = `Bearer ${tokenToUse}`
        }
        
        // Skip /auth/me call - we'll only call it when explicitly needed
        // This prevents unnecessary API calls before every request
      }

      // send the system timezone in query params
      if (config.params) {
        config.params['timezone'] = userTimezone // Append to query parameters if any
      } else {
        config.params = {timezone: userTimezone} // If no params, create new
      }

      // Check if this is a multipart/form-data request
      if (config.data instanceof FormData && config.headers) {
        config.headers['Content-Type'] = 'multipart/form-data'
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )

  axios.interceptors.response.use(
    (response: any) => {
      // 🔥 REAL-TIME USER STATUS CHECK: Check user status on every API response
      const userData = response?.data?.data?.user || response?.data?.user
      if (userData && userData.status === 0) {
        localStorage.clear()
        sessionStorage.clear()
        navigateToLogin()
        return Promise.reject(new Error('User account has been disabled'))
      }

      // If the backend returns status "Fail" and message about token not found or expired
      if (
        response?.data?.status === 'Fail' &&
        response?.data?.message &&
        (response.data.message.toLowerCase().includes('token not found') ||
          response.data.message.toLowerCase().includes('no user was found.'))
      ) {
        // Clear token from storage if needed
        localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
        // Force redirect to login
        navigateToLogin()
        return Promise.reject(response)
      }
      return response
    },
    (error: any) => {
      console.log('🔥 [AuthHelpers] Interceptor error:', {
        status: error?.response?.status,
        message: error?.response?.data?.message,
        url: error?.config?.url
      })

      // 🔥 REAL-TIME USER STATUS CHECK: Check user status in error responses too
      const userData = error?.response?.data?.data?.user || error?.response?.data?.user
      if (userData && userData.status === 0) {
        console.log('🔥 [AuthHelpers] User disabled, logging out')
        localStorage.clear()
        sessionStorage.clear()
        navigateToLogin()
        return Promise.reject(new Error('User account has been disabled'))
      }

      // Handle 401 Unauthorized responses (token invalid/deleted from server)
      if (error?.response?.status === 401) {
        console.log('🔥 [AuthHelpers] 401 error, logging out')
        localStorage.clear()
        sessionStorage.clear()
        navigateToLogin()
        return Promise.reject(error)
      }

      // Handle 403 errors more intelligently
      if (error?.response?.status === 403) {
        const errorMessage = error?.response?.data?.message?.toLowerCase() || ''
        
        // Only logout for authentication-related 403 errors
        const isAuthenticationError = 
          errorMessage.includes('token not found') ||
          errorMessage.includes('no user was found') ||
          errorMessage.includes('invalid token') ||
          errorMessage.includes('token expired') ||
          errorMessage.includes('unauthorized') ||
          errorMessage.includes('authentication failed')
        
        // Do NOT logout for resource-specific access errors
        const isResourceAccessError = 
          errorMessage.includes('billing personnel is not linked') ||
          errorMessage.includes('not linked') ||
          errorMessage.includes('insufficient permissions') ||
          errorMessage.includes('access denied') ||
          errorMessage.includes('forbidden') ||
          errorMessage.includes('must select a doctor') ||
          errorMessage.includes('no access to this resource')
        
        console.log('🔥 [AuthHelpers] 403 error analysis:', {
          message: errorMessage,
          isAuthenticationError,
          isResourceAccessError,
          willLogout: isAuthenticationError && !isResourceAccessError
        })
        
        if (isAuthenticationError && !isResourceAccessError) {
          console.log('🔥 [AuthHelpers] Authentication-related 403, logging out')
          localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
          navigateToLogin()
        } else {
          console.log('🔥 [AuthHelpers] Resource-specific 403, NOT logging out')
        }
        
        return Promise.reject(error)
      }

      // Handle specific token-related error messages
      if (
        error?.response?.data?.message &&
        (error.response.data.message.toLowerCase().includes('token not found') ||
         error.response.data.message.toLowerCase().includes('no user was found.'))
      ) {
        console.log('🔥 [AuthHelpers] Token-related error, logging out')
        localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
        navigateToLogin()
      }
      
      return Promise.reject(error)
    }
  )
}

export {getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY}
